import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, Statistic, Typography, Row, Col, Button } from 'antd';
import { GetAPI } from '../../../redux';
import { useDispatch } from 'react-redux';
import { RedoOutlined } from '@ant-design/icons';
import {
    RiExchangeDollarLine,
    RiCheckboxCircleLine,
    RiErrorWarningLine,
    RiFlightTakeoffFill,
    RiFlightLandFill,
    RiStackshareLine
} from 'react-icons/ri';
import { BaseColor } from '../../../config/env';
import TablePage from '../../../components/pages/TablePage';

const { Title, Text } = Typography;

const defaultMonit = {
    export: { success: 0, error: 0 },
    import: { success: 0, error: 0 },
    distribusi: { success: 0, error: 0 }
};

const hexToRgba = (hex = '#118B50', alpha = 1) => {
    const sanitized = hex.replace('#', '');
    const full = sanitized.length === 3
        ? sanitized.split('').map(ch => `${ch}${ch}`).join('')
        : sanitized.padEnd(6, '0');
    const num = parseInt(full, 16);
    const r = (num >> 16) & 255;
    const g = (num >> 8) & 255;
    const b = num & 255;
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const createStyles = (baseColor) => ({
    page: {
        position: 'relative',
        padding: 32,
        borderRadius: 20,
        minHeight: '60vh'
    },
    glowOne: {
        display: 'none'
    },
    glowTwo: {
        display: 'none'
    },
    content: {
        position: 'relative',
        zIndex: 2
    },
    hero: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 16,
        marginBottom: 32
    },
    heroTitle: {
        color: '#0f172a',
        margin: 0
    },
    heroSubtitle: {
        color: '#475467',
        margin: 0
    },
    refreshButton: {
        borderColor: hexToRgba(baseColor, 0.35),
        color: baseColor,
        borderRadius: 999,
        paddingInline: 20,
        boxShadow: '0 1px 2px rgba(16, 24, 40, 0.08)'
    },
    card: () => ({
        borderRadius: 22,
        border: `1px solid ${hexToRgba(baseColor, 0.25)}`,
        background: `linear-gradient(140deg, ${hexToRgba(baseColor, 0.22)}, rgba(255,255,255,0.9))`,
        boxShadow: `0 25px 45px ${hexToRgba(baseColor, 0.25)}`,
        backdropFilter: 'blur(18px)'
    }),
    cardHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 16
    },
    cardTitle: {
        color: '#101828',
        fontSize: 16,
        margin: 0
    },
    cardSubtitle: {
        color: '#475467',
        fontSize: 13
    },
    accentBubble: () => ({
        width: 44,
        height: 44,
        borderRadius: '50%',
        background: hexToRgba(baseColor, 0.3),
        border: `1px solid ${hexToRgba(baseColor, 0.45)}`,
        boxShadow: `0 10px 25px ${hexToRgba(baseColor, 0.35)}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    }),
    statRow: {
        display: 'flex',
        justifyContent: 'space-between',
        gap: 12,
        marginTop: 8
    },
    statPill: {
        flex: 1,
        padding: '10px 12px',
        borderRadius: 14,
        border: `1px solid ${hexToRgba(baseColor, 0.15)}`,
        background: '#f8fafc',
        color: '#0f172a',
        textAlign: 'center',
        fontSize: 13
    },
    progressShell: {
        width: '100%',
        height: 10,
        borderRadius: 999,
        background: '#ffffff',
        overflow: 'hidden',
        marginTop: 12
    },
    progressBar: (width) => ({
        width: `${width}%`,
        height: '100%',
        background: hexToRgba(baseColor, 0.85),
        transition: 'width 0.4s ease'
    })
});

export default () => {
    const dispatch = useDispatch();
    const [datamonit, setDataMonit] = useState(defaultMonit);

    const accentColor = BaseColor || '#118B50';
    const styles = useMemo(() => createStyles(accentColor), [accentColor]);

    const getMonit = useCallback((value) => {
        dispatch(GetAPI({ url: 'api/v1/monitoring/display', data: value }))
            .then(resp => {
                setDataMonit(resp?.payload?.data || defaultMonit);
            });
    }, [dispatch]);

    useEffect(() => {
        getMonit();
    }, [getMonit]);

    const cards = [
        { key: 'export', title: 'Export', Icon: RiFlightTakeoffFill },
        { key: 'import', title: 'Import', Icon: RiFlightLandFill },
        { key: 'distribusi', title: 'Distribusi', Icon: RiStackshareLine }
    ];

    const DashboardCard = ({ title, Icon, successCount = 0, errorCount = 0 }) => {
        const total = successCount + errorCount;
        const successRate = total ? Math.round((successCount / total) * 100) : 0;
        const errorRate = total ? 100 - successRate : 0;

        return (
            <Card
                hoverable
                bordered={false}
                style={styles.card()}
                bodyStyle={{ padding: 24 }}
            >
                <div style={styles.cardHeader}>
                    <div>
                        <p style={styles.cardTitle}>{title}</p>
                        <Text style={styles.cardSubtitle}>Total send data</Text>
                        <Title level={2} style={{ color: '#0f172a', margin: '4px 0 0' }}>{total}</Title>
                    </div>
                    <div style={styles.accentBubble()}>
                        {Icon && <Icon color={BaseColor} size={22} />}
                    </div>
                </div>
                <div>
                    <Statistic
                        title={(
                            <span style={{ color: 'rgba(148,163,184,0.9)', display: 'flex', alignItems: 'center', gap: 6 }}>
                                <RiCheckboxCircleLine color="#22c55e" />
                                Success
                            </span>
                        )}
                        value={successCount}
                        valueStyle={{ color: '#4ade80', fontSize: 26 }}
                    />
                    <Statistic
                        title={(
                            <span style={{ color: 'rgba(148,163,184,0.9)', display: 'flex', alignItems: 'center', gap: 6 }}>
                                <RiErrorWarningLine color="#f87171" />
                                Error
                            </span>
                        )}
                        value={errorCount}
                        valueStyle={{ color: '#f87171', fontSize: 26 }}
                    />
                </div>
                <div style={styles.progressShell}>
                    <div style={styles.progressBar(successRate)} />
                </div>
                <div style={styles.statRow}>
                    <div style={styles.statPill}>
                        Success Rate<br />
                        <strong>{successRate}%</strong>
                    </div>
                    <div style={styles.statPill}>
                        Error Rate<br />
                        <strong>{errorRate}%</strong>
                    </div>
                </div>
            </Card>
        );
    };

    const columns = [
        {
            selector: (row) => row?.name,
            name: "Name",
            id: 'name'
        },
        {
            selector: (row) => row?.total,
            name: "Total",
            id: 'total'
        },
    ]

    return (
        <div style={styles.page}>
            <div style={styles.glowOne} />
            <div style={styles.glowTwo} />
            <div style={styles.content}>
                <div style={styles.hero}>
                    <div>
                        <Title level={3} style={styles.heroTitle}>Display Monitoring</Title>
                        <Text style={styles.heroSubtitle}>
                            Monitor the status of sending Export, Import, and Distribution data.
                        </Text>
                    </div>
                    <Button
                        ghost
                        icon={<RedoOutlined />}
                        size="large"
                        style={styles.refreshButton}
                        onClick={() => getMonit()}
                    >
                        Refresh data
                    </Button>
                </div>
                <Row gutter={[24, 24]}>
                    {cards.map(({ key, title, Icon }) => (
                        <Col xs={24} sm={24} md={12} lg={8} key={key}>
                            <DashboardCard
                                title={title}
                                Icon={Icon}
                                successCount={datamonit?.[key]?.success || 0}
                                errorCount={datamonit?.[key]?.error || 0}
                            />
                        </Col>
                    ))}
                </Row>

                <div style={{ marginTop: 40 }}>
                    <TablePage
                        title={"PJT Gate Out"}
                        url="api/v1/tps_import/dashboard_import_out_pjt"
                        actionPosition={'first'}
                        columns={columns}
                    />
                </div>
            </div>
        </div>
    );
};